/**
 * Registered statistics — `server/app/routers/stats.py`, built earlier this
 * session and unwired to any screen until now. Chunk 9's whole job here is
 * the read: the registry, the caveats, the deterministic queries all already
 * exist server-side (I9).
 *
 * `useStatQuery`'s `query_id` param can be `null` — the Statistics workspace
 * shows the catalogue first and runs a query only once a person picks one, so
 * this hook stays disabled rather than firing on every catalogue row.
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query'

import { api } from './client'
import type { RegisteredQueryList, StatQueryResult } from './types'

export const statsKeys = {
  queries: (office: string | null, ground: string | null) =>
    ['stats', 'queries', office ?? '', ground ?? ''] as const,
  result: (queryId: string | null) => ['stats', 'result', queryId ?? ''] as const,
}

/** The catalogue — every registered `query_id`, with its caveat. */
export function useRegisteredQueries(
  office: string | null,
  ground: string | null,
): UseQueryResult<RegisteredQueryList> {
  return useQuery<RegisteredQueryList>({
    queryKey: statsKeys.queries(office, ground),
    queryFn: () =>
      api.get<RegisteredQueryList>('/api/stats/queries', {
        query: { office: office ?? undefined, ground: ground ?? undefined },
      }),
  })
}

/** Run one registered statistic. A 200 with `refused: true` is a real answer
 * (a `corpus_sample` basis id), not an error — never thrown as one. */
export function useStatQuery(queryId: string | null): UseQueryResult<StatQueryResult> {
  return useQuery<StatQueryResult>({
    queryKey: statsKeys.result(queryId),
    queryFn: () => api.get<StatQueryResult>(`/api/stats/${encodeURIComponent(queryId ?? '')}`),
    enabled: Boolean(queryId),
  })
}
